"use client";

import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import { navItems } from "@/lib/shared/constants";
import {
  cn,
  HStack,
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuIndicator,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/ui";
import { preventCurrentRouteNavigation, RouteLoadingLinkStatus } from "./RouteLoadingSpinner";

interface DesktopNavItemsProps {
  compact?: boolean;
}

// Style constants based on compact mode
const DIMENSIONS = {
  compact: {
    height: "h-8",
    padding: "px-3 py-1",
    fontSize: "text-[10px]",
    tracking: "tracking-[0.1em]",
    underlineOffset: "after:bottom-0 after:left-3 after:right-3",
  },
  expanded: {
    height: "h-10",
    padding: "px-5 py-2",
    fontSize: "text-[11px]",
    tracking: "tracking-[0.15em]",
    underlineOffset: "after:bottom-1 after:left-5 after:right-5",
  },
};

const DROPDOWN_WIDTH = {
  compact: "min-w-[120px]",
  expanded: "min-w-[140px]",
};

/**
 * Builds the luxury nav trigger style with gold underline reveal
 */
function buildNavTriggerStyle(compact: boolean): string {
  const dims = compact ? DIMENSIONS.compact : DIMENSIONS.expanded;

  return cn(
    "group relative inline-flex w-max items-center justify-center",
    dims.height,
    dims.padding,
    dims.fontSize,
    dims.tracking,
    "font-medium uppercase",
    "text-muted-foreground",
    "transition-colors duration-500",
    // Override NavigationMenuTrigger defaults (bg-background / accent) so trigger matches glass dropdown
    "bg-transparent hover:bg-transparent focus:bg-transparent focus-visible:bg-transparent",
    "data-[state=open]:bg-transparent data-[state=open]:hover:bg-transparent data-[state=open]:focus:bg-transparent",
    "hover:text-foreground",
    "focus:outline-none",
    "disabled:pointer-events-none disabled:opacity-50",
    // Gold underline animation
    "after:absolute after:h-px after:bg-primary",
    "after:scale-x-0 after:origin-left",
    "after:transition-transform after:duration-500 after:ease-out",
    "hover:after:scale-x-100",
    "data-[active]:text-foreground data-[state=open]:text-foreground",
    "data-[active]:after:scale-x-100 data-[state=open]:after:scale-x-100",
    dims.underlineOffset,
  );
}

/**
 * Dropdown link styles with vertical gold line indicator
 */
const DROPDOWN_LINK_STYLES = cn(
  "group relative flex w-full items-center px-5 py-3",
  "text-sm font-normal tracking-wide",
  "text-muted-foreground",
  "transition-all duration-300",
  "hover:text-foreground hover:bg-muted/50",
  // Vertical gold line indicator on hover
  "before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2",
  "before:h-0 before:w-px before:bg-primary",
  "before:transition-all before:duration-300",
  "hover:before:h-4",
);

/**
 * DesktopNavItems component - Desktop navigation menu items
 * Luxury styling: underline reveal on hover, minimal chrome, elegant transitions
 */
export const DesktopNavItems = ({ compact = false }: DesktopNavItemsProps) => {
  const t = useTranslations("navigation");
  const navTriggerStyle = buildNavTriggerStyle(compact);
  const dropdownWidth = compact ? DROPDOWN_WIDTH.compact : DROPDOWN_WIDTH.expanded;

  return (
    <HStack className="items-center justify-center">
      <NavigationMenu>
        <NavigationMenuList className="gap-0">
          {navItems.map((item) => {
            if (item.subItems) {
              return (
                <NavigationMenu
                  key={item.name}
                  delayDuration={0}
                >
                  <NavigationMenuItem>
                    <NavigationMenuTrigger className={navTriggerStyle}>
                      {t(item.name)}
                    </NavigationMenuTrigger>
                    <NavigationMenuContent className="bg-popover/98 backdrop-blur-md border-0 shadow-xl">
                      <div className={cn("grid gap-1 w-fit py-2", dropdownWidth)}>
                        {item.subItems.map((subItem) => (
                          <DropdownLink
                            key={subItem.name}
                            subItem={subItem}
                            t={t}
                          />
                        ))}
                      </div>
                    </NavigationMenuContent>
                  </NavigationMenuItem>
                </NavigationMenu>
              );
            }
            return (
              <NavigationMenuItem key={item.name}>
                <NavigationMenuLink asChild>
                  <Link
                    href={item.href ?? "/"}
                    className={navTriggerStyle}
                    onClickCapture={preventCurrentRouteNavigation}
                  >
                    {t(item.name)}
                    <RouteLoadingLinkStatus />
                  </Link>
                </NavigationMenuLink>
              </NavigationMenuItem>
            );
          })}
        </NavigationMenuList>
        <NavigationMenuIndicator />
      </NavigationMenu>
    </HStack>
  );
};

interface DropdownLinkProps {
  subItem: { name: string; href?: string };
  t: (key: string) => string;
}

/**
 * DropdownLink - Individual link in dropdown menu
 */
function DropdownLink({ subItem, t }: DropdownLinkProps) {
  const href = subItem.href ?? "/";

  return (
    <NavigationMenuLink
      asChild
      className={DROPDOWN_LINK_STYLES}
    >
      <Link
        href={href}
        onClickCapture={preventCurrentRouteNavigation}
      >
        {t(subItem.name)}
        <RouteLoadingLinkStatus />
      </Link>
    </NavigationMenuLink>
  );
}
